import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap } from 'rxjs/operators';
import { Action } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { CatalogFacade } from '../facades/catalog.facade';
import * as CatalogActions from '../actions/catalog.action';

@Injectable()
export class CatalogEffects {
  private readonly actions$: Actions = inject(Actions);
  private readonly catalogFacade: CatalogFacade = inject(CatalogFacade);

  readonly loadHowManyProductsShouldBeCached$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CatalogActions.loadHowManyProductShouldBeCached),
      exhaustMap(() =>
        this.catalogFacade.getHowManyProductsShouldBeCached()
          .pipe(
            map(count => CatalogActions.loadHowManyProductShouldBeCachedSuccess({count})),
            catchError(() => of(CatalogActions.loadHowManyProductShouldBeCachedError())),
          )),
    );
  });

  readonly loadAbstractProductData$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CatalogActions.loadAbstractProductData),
      exhaustMap(action =>
        this.catalogFacade.getAbstractProductDataFromApi(action.sku)
          .pipe(
            map(payload => CatalogActions.loadAbstractProductDataSuccess({payload})),
            catchError(error => of(CatalogActions.loadAbstractProductDataError({error}))),
          )),
    );
  });

  readonly loadAbstractProductPricesAndAvailabilities$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CatalogActions.loadAbstractProductInfoFromSap),
      mergeMap(action =>
        this.catalogFacade.getAbstractProductPricesAndAvailabilitiesFromSap(action.sku, action.uuid)
          .pipe(
            map(productData => CatalogActions.loadAbstractProductInfoFromSapSuccess({payload: productData})),
            catchError(error => of(CatalogActions.loadAbstractProductInfoFromSapError({error: error}))),
          )
      )
    );
  });


  readonly loadCategories$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogActions.loadCategoriesStart),
      mergeMap(
        () => this.catalogFacade.getCategoryTree()
          .pipe(
            map(data => {
              return CatalogActions.loadCategoriesSuccess({payload: data});
            }),
            catchError(error => of(CatalogActions.loadCategoriesFail({error})),
            ),
          ),
      ),
    ),
  );

  readonly sdmIbComponentsData$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogActions.SdmIbComponentsActionStart),
      exhaustMap(action =>
         this.catalogFacade.getSdmIbComponents(action.functionalLocation)
          .pipe(
            map((data) =>
              CatalogActions.SuccessSdmIbComponentsAction({payload: data}),
            ),
            catchError(error => of(CatalogActions.SdmIbComponentsActionFailed({error}))),
          ),
      ),
    )
  );
}
